import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import AdminLayout from "@/components/feature/AdminLayout";
import { supabase } from "@/lib/supabase";
import {
  getAllProducts,
  getAllProfiles,
  createOrderFromCart,
  toMoney,
} from "@/services/supabaseService";
import type { Product } from "@/lib/supabase";
import type { Profile } from "@/lib/supabase";

interface SelectedItem {
  productId: string;
  product: Product;
  quantity: number;
}

interface RecentOrder {
  id: string;
  user_name: string;
  total_amount: number;
  status: string;
  created_at: string;
  item_count: number;
}

function getPriceForUser(product: Product, user: Profile | null): number {
  if (!user || user.role === "admin") return product.distribution_price;
  switch (user.level) {
    case "核心分銷":
      return product.core_price || product.distribution_price;
    case "特約經銷":
      return product.dealer_price || product.distribution_price;
    case "普通分銷":
      return product.normal_price || product.distribution_price;
    default:
      return product.distribution_price;
  }
}

function getProfitForUser(product: Product, user: Profile | null): number {
  return product.suggested_price - getPriceForUser(product, user);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ManualOrder() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [members, setMembers] = useState<Profile[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedMember, setSelectedMember] = useState<Profile | null>(null);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [regionFilter, setRegionFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [pList, mList] = await Promise.all([
        getAllProducts(),
        getAllProfiles(),
      ]);
      const activeProducts = pList.filter((p) => p.status !== "下架");
      const distributors = mList.filter((m) => m.role !== "admin");
      setProducts(activeProducts);
      setMembers(distributors);
    } catch {
      setError("載入資料失敗，請重新整理頁面");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadRecentOrders = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const { data: ordersData, error: ordersErr } = await supabase
        .from("orders")
        .select("id, user_name, total_amount, status, created_at")
        .order("created_at", { ascending: false })
        .limit(10);
      if (ordersErr || !ordersData) {
        setRecentOrders([]);
        return;
      }
      const orderIds = ordersData.map((o) => o.id);
      const { data: itemsData, error: itemsErr } = await supabase
        .from("order_items")
        .select("order_id, quantity")
        .in("order_id", orderIds);
      if (itemsErr || !itemsData) {
        setRecentOrders([]);
        return;
      }
      const itemCounts: Record<string, number> = {};
      itemsData.forEach((item) => {
        itemCounts[item.order_id] = (itemCounts[item.order_id] || 0) + item.quantity;
      });
      const mapped: RecentOrder[] = ordersData.map((o) => ({
        id: o.id,
        user_name: o.user_name,
        total_amount: Number(o.total_amount),
        status: o.status || "待發貨",
        created_at: o.created_at,
        item_count: itemCounts[o.id] || 0,
      }));
      setRecentOrders(mapped);
    } catch {
      setRecentOrders([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadRecentOrders();
  }, [loadData, loadRecentOrders]);

  const regions = useMemo(() => {
    const set = new Set(products.map((p) => p.region).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchSearch =
        !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchRegion = regionFilter === "all" || p.region === regionFilter;
      return matchSearch && matchRegion;
    });
  }, [products, searchQuery, regionFilter]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return members;
    const q = memberSearch.toLowerCase();
    return members.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.phone?.includes(q)
    );
  }, [members, memberSearch]);

  const totalAmount = selectedItems.reduce(
    (sum, item) =>
      sum + Math.round(getPriceForUser(item.product, selectedMember) * item.quantity),
    0
  );

  const totalSuggested = selectedItems.reduce(
    (sum, item) => sum + Math.round(item.product.suggested_price * item.quantity),
    0
  );

  const totalProfit = totalSuggested - totalAmount;

  const memberBalance = selectedMember ? toMoney(selectedMember.balance) : 0;
  const balanceEnough = memberBalance >= totalAmount;

  const totalSelectedGroups = selectedItems.reduce((sum, i) => sum + i.quantity, 0);
  const isNormalDistributor = selectedMember?.level === "普通分銷";
  const normalLimitExceeded = isNormalDistributor && totalSelectedGroups > 10;

  const addItem = (product: Product, quantity: number) => {
    if (quantity <= 0) return;
    if (product.stock > 0 && quantity > product.stock) {
      setError(`「${product.name}」庫存僅剩 ${product.stock} 件`);
      setTimeout(() => setError(""), 3000);
      return;
    }
    setSelectedItems((prev) => {
      const existing = prev.find((i) => i.productId === product.id);
      if (existing) {
        const newQty = existing.quantity + quantity;
        if (product.stock > 0 && newQty > product.stock) {
          setError(`「${product.name}」庫存僅剩 ${product.stock} 件`);
          setTimeout(() => setError(""), 3000);
          return prev;
        }
        return prev.map((i) =>
          i.productId === product.id ? { ...i, quantity: newQty } : i
        );
      }
      return [...prev, { productId: product.id, product, quantity }];
    });
  };

  const updateItemQty = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      setSelectedItems((prev) => prev.filter((i) => i.productId !== productId));
      return;
    }
    const item = selectedItems.find((i) => i.productId === productId);
    if (item && item.product.stock > 0 && quantity > item.product.stock) {
      setError(`「${item.product.name}」庫存僅剩 ${item.product.stock} 件`);
      setTimeout(() => setError(""), 3000);
      return;
    }
    setSelectedItems((prev) =>
      prev.map((i) => (i.productId === productId ? { ...i, quantity } : i))
    );
  };

  const removeItem = (productId: string) => {
    setSelectedItems((prev) => prev.filter((i) => i.productId !== productId));
  };

  const clearAllItems = () => {
    setSelectedItems([]);
  };

  const handleSubmit = async () => {
    if (!selectedMember) {
      setError("請先選擇分銷商");
      return;
    }
    if (selectedItems.length === 0) {
      setError("請至少選擇一項商品");
      return;
    }
    if (!balanceEnough) {
      setError(
        `分銷商餘額不足（目前 $${memberBalance}，需要 $${totalAmount}）`
      );
      return;
    }
    if (normalLimitExceeded) {
      setError("普通分銷全站限購 10 組商品");
      return;
    }

    if (isNormalDistributor) {
      const restricted = selectedItems.filter(
        (item) => item.product.region !== "體驗認購區"
      );
      if (restricted.length > 0) {
        const names = restricted.map((i) => `「${i.product.name}」`).join("、");
        setError(`此區域僅限 VIP 會員認購：${names}`);
        return;
      }
    }

    setIsSubmitting(true);
    setError("");
    try {
      const cartItems = selectedItems.map((item) => ({
        productId: item.productId,
        productName: item.product.name,
        productImage: item.product.images[0] || "",
        distributionPrice: getPriceForUser(item.product, selectedMember),
        suggestedPrice: item.product.suggested_price,
        quantity: item.quantity,
      }));

      const result = await createOrderFromCart(
        selectedMember.id,
        selectedMember.name || selectedMember.email || "未知用戶",
        cartItems
      );

      if (!result.success) {
        setError(result.error || "下單失敗");
        setIsSubmitting(false);
        return;
      }

      setSuccessMsg(
        `訂單 ${result.orderId} 建立成功！已從 ${selectedMember.name || selectedMember.email} 的錢包扣除 $${totalAmount}`
      );
      setSelectedItems([]);
      setSelectedMember(null);
      setMemberSearch("");
      const updatedMembers = await getAllProfiles();
      setMembers(updatedMembers.filter((m) => m.role !== "admin"));
      await loadRecentOrders();
      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "下單失敗");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case "已完成":
        return "bg-emerald-50 text-emerald-700 border-emerald-100";
      case "已發貨":
        return "bg-blue-50 text-blue-700 border-blue-100";
      case "待發貨":
      default:
        return "bg-amber-50 text-amber-700 border-amber-100";
    }
  };

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer"
          >
            返回首頁
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">手動幫客戶下單</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            選擇分銷商並挑選商品，系統將自動從該分銷商餘額扣款並建立訂單
          </p>
        </div>

        {/* Member selector */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <label className="text-sm font-semibold text-gray-700 mb-2 block">
            選擇分銷商
          </label>
          <div className="relative max-w-md">
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus-within:border-amber-400 transition-colors">
              <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-sm"></i>
              </div>
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => {
                  setMemberSearch(e.target.value);
                  setShowMemberDropdown(true);
                  if (selectedMember) {
                    setSelectedMember(null);
                  }
                }}
                onFocus={() => setShowMemberDropdown(true)}
                placeholder="搜尋分銷商名稱、Email 或電話..."
                className="flex-1 text-sm outline-none bg-transparent"
              />
              {selectedMember && (
                <button
                  onClick={() => {
                    setSelectedMember(null);
                    setMemberSearch("");
                  }}
                  className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}
            </div>

            {showMemberDropdown && !selectedMember && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMemberDropdown(false)}
                />
                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-100 rounded-lg shadow-lg">
                  {filteredMembers.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-400">
                      找不到符合的分銷商
                    </div>
                  ) : (
                    filteredMembers.slice(0, 50).map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          setSelectedMember(m);
                          setMemberSearch(m.name || m.email || "");
                          setShowMemberDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0 cursor-pointer flex items-center gap-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-xs font-medium flex-shrink-0">
                          {(m.name || m.email || "?").charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {m.name || "未設定名稱"}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {m.email} · {m.level || "普通分銷"} · 餘額 $
                            {toMoney(m.balance).toLocaleString()}
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {selectedMember && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-100">
                <div className="w-4 h-4 flex items-center justify-center text-amber-600">
                  <i className="ri-user-line text-xs"></i>
                </div>
                <span className="text-sm font-medium text-amber-700">
                  {selectedMember.name || selectedMember.email}
                </span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    selectedMember.level === "核心分銷"
                      ? "bg-purple-100 text-purple-700"
                      : selectedMember.level === "特約經銷"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {selectedMember.level || "普通分銷"}
                </span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
                <div className="w-4 h-4 flex items-center justify-center text-emerald-600">
                  <i className="ri-wallet-3-line text-xs"></i>
                </div>
                <span className="text-sm text-emerald-700">
                  錢包餘額{" "}
                  <span className="font-bold">
                    ${toMoney(selectedMember.balance).toLocaleString()}
                  </span>
                </span>
              </div>
              {selectedMember.level === "普通分銷" && (
                <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                  普通分銷全站限購 10 組 · 僅限體驗認購區
                </span>
              )}
            </div>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600 flex items-start gap-2">
            <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
              <i className="ri-error-warning-line text-red-500"></i>
            </div>
            <span>{error}</span>
          </div>
        )}
        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700 flex items-start gap-2">
            <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
              <i className="ri-check-line text-emerald-600"></i>
            </div>
            <span>{successMsg}</span>
          </div>
        )}

        {/* Main content: products + cart summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Products list */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line text-sm"></i>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜尋商品名稱..."
                  className="flex-1 text-sm outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {regions.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRegionFilter(r)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      regionFilter === r
                        ? "bg-amber-500 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {r === "all" ? "全部區域" : r}
                  </button>
                ))}
              </div>
            </div>

            {/* Product grid */}
            {isLoading ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-sm text-gray-400">
                載入商品中...
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-2xl text-gray-300"></i>
                </div>
                <p className="text-gray-500 text-sm">暫無符合條件的商品</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredProducts.map((product) => {
                  const price = getPriceForUser(product, selectedMember);
                  const profit = getProfitForUser(product, selectedMember);
                  const alreadyInCart = selectedItems.find(
                    (i) => i.productId === product.id
                  );
                  const qtyInCart = alreadyInCart?.quantity || 0;
                  const maxStock = product.stock > 0 ? product.stock : 999;
                  const remainingStock = maxStock - qtyInCart;

                  return (
                    <div
                      key={product.id}
                      className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex gap-3"
                    >
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-800 line-clamp-1">
                          {product.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            {product.region || "一般區"}
                          </span>
                          <span className="text-xs text-gray-400">
                            庫存 {product.stock > 0 ? product.stock : "∞"}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-2 mt-1.5">
                          <span className="text-sm font-bold text-amber-600">
                            ${price.toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-400 line-through">
                            原價 ${product.suggested_price.toLocaleString()}
                          </span>
                        </div>
                        <p className="text-xs text-emerald-600 mt-0.5">
                          利潤 ${profit.toLocaleString()}
                        </p>

                        <div className="flex items-center gap-2 mt-3">
                          {alreadyInCart ? (
                            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                              <button
                                onClick={() =>
                                  updateItemQty(product.id, qtyInCart - 1)
                                }
                                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer"
                              >
                                <i className="ri-subtract-line text-xs"></i>
                              </button>
                              <span className="w-8 text-center text-sm">
                                {qtyInCart}
                              </span>
                              <button
                                onClick={() => {
                                  if (remainingStock <= 0) {
                                    setError(
                                      `「${product.name}」庫存不足`
                                    );
                                    setTimeout(() => setError(""), 3000);
                                    return;
                                  }
                                  updateItemQty(product.id, qtyInCart + 1);
                                }}
                                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer"
                              >
                                <i className="ri-add-line text-xs"></i>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => addItem(product, 1)}
                              disabled={
                                product.stock > 0 && remainingStock <= 0
                              }
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                            >
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-add-line text-[10px]"></i>
                              </div>
                              加入清單
                            </button>
                          )}
                          {qtyInCart > 0 && (
                            <button
                              onClick={() => removeItem(product.id)}
                              className="text-xs text-red-500 hover:text-red-600 cursor-pointer"
                            >
                              移除
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recent order history */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-900">
                  最近下單紀錄
                </h2>
                <button
                  onClick={() => loadRecentOrders()}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-600 cursor-pointer transition-colors"
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-refresh-line text-[10px]"></i>
                  </div>
                  重新整理
                </button>
              </div>
              {isLoadingHistory ? (
                <div className="text-center py-6 text-sm text-gray-400">
                  載入中...
                </div>
              ) : recentOrders.length === 0 ? (
                <div className="text-center py-6">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                    <i className="ri-time-line text-xl text-gray-300"></i>
                  </div>
                  <p className="text-sm text-gray-400">暫無下單紀錄</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentOrders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-xs font-medium flex-shrink-0">
                          {(order.user_name || "?").charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {order.user_name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDateTime(order.created_at)} ·{" "}
                            {order.item_count} 組商品
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-sm font-medium text-gray-700">
                          ${order.total_amount.toLocaleString()}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border ${statusBadgeClass(
                            order.status
                          )}`}
                        >
                          {order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Order summary panel */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-900">
                  訂單摘要
                </h2>
                {selectedItems.length > 0 && (
                  <button
                    onClick={clearAllItems}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 cursor-pointer transition-colors"
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-[10px]"></i>
                    </div>
                    一鍵清空
                  </button>
                )}
              </div>

              {selectedItems.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                    <i className="ri-shopping-cart-line text-xl text-gray-300"></i>
                  </div>
                  <p className="text-sm text-gray-400">尚未選擇任何商品</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {selectedItems.map((item) => {
                    const price = getPriceForUser(item.product, selectedMember);
                    return (
                      <div
                        key={item.productId}
                        className="flex gap-2 pb-3 border-b border-gray-50 last:border-0"
                      >
                        <img
                          src={item.product.images[0]}
                          alt={item.product.name}
                          className="w-10 h-10 rounded-md object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">
                            {item.product.name}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
                              <button
                                onClick={() =>
                                  updateItemQty(
                                    item.productId,
                                    item.quantity - 1
                                  )
                                }
                                className="w-5 h-5 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer"
                              >
                                <i className="ri-subtract-line text-[10px]"></i>
                              </button>
                              <input
                                type="number"
                                min={1}
                                max={item.product.stock > 0 ? item.product.stock : 9999}
                                value={item.quantity}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  if (Number.isNaN(val)) return;
                                  const maxStock =
                                    item.product.stock > 0
                                      ? item.product.stock
                                      : 9999;
                                  if (val > maxStock) {
                                    setError(
                                      `「${item.product.name}」庫存僅剩 ${item.product.stock} 件`
                                    );
                                    setTimeout(() => setError(""), 3000);
                                    updateItemQty(item.productId, maxStock);
                                    return;
                                  }
                                  updateItemQty(item.productId, val);
                                }}
                                className="w-10 text-center text-xs outline-none border-0 bg-transparent"
                              />
                              <button
                                onClick={() =>
                                  updateItemQty(
                                    item.productId,
                                    item.quantity + 1
                                  )
                                }
                                className="w-5 h-5 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer"
                              >
                                <i className="ri-add-line text-[10px]"></i>
                              </button>
                            </div>
                            <span className="text-xs font-medium text-gray-700">
                              ${(price * item.quantity).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.productId)}
                          className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-red-500 self-start cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedItems.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">認購總額</span>
                    <span className="font-medium text-gray-700">
                      ${totalAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">銷售總額</span>
                    <span className="font-medium text-gray-700">
                      ${totalSuggested.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600 font-medium">
                      預估利潤
                    </span>
                    <span className="font-bold text-emerald-600">
                      +${totalProfit.toLocaleString()}
                    </span>
                  </div>

                  {selectedMember && (
                    <div className="mt-3 p-3 bg-stone-50 rounded-lg space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          {selectedMember.name || selectedMember.email} 餘額
                        </span>
                        <span
                          className={`text-xs font-medium ${
                            balanceEnough ? "text-emerald-600" : "text-red-500"
                          }`}
                        >
                          ${memberBalance.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">扣款後餘額</span>
                        <span
                          className={`text-xs font-bold ${
                            memberBalance - totalAmount < 0
                              ? "text-red-600"
                              : "text-gray-700"
                          }`}
                        >
                          ${Math.max(0, memberBalance - totalAmount).toLocaleString()}
                        </span>
                      </div>
                      {!balanceEnough && (
                        <p className="text-xs text-red-500 mt-1">
                          餘額不足，無法下單
                        </p>
                      )}
                    </div>
                  )}

                  {normalLimitExceeded && (
                    <p className="text-xs text-red-500 bg-red-50 px-2 py-1.5 rounded-lg">
                      普通分銷全站限購 10 組（目前已選 {totalSelectedGroups} 組）
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={
                  selectedItems.length === 0 ||
                  !selectedMember ||
                  !balanceEnough ||
                  normalLimitExceeded ||
                  isSubmitting
                }
                className="w-full mt-4 py-3 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center animate-spin">
                      <i className="ri-loader-2-line text-sm"></i>
                    </div>
                    處理中...
                  </span>
                ) : !selectedMember ? (
                  "請先選擇分銷商"
                ) : selectedItems.length === 0 ? (
                  "請選擇商品"
                ) : !balanceEnough ? (
                  "餘額不足"
                ) : normalLimitExceeded ? (
                  "超出限購數量"
                ) : (
                  "確認下單並扣款"
                )}
              </button>

              <p className="mt-2 text-xs text-gray-400 text-center">
                下單後訂單狀態為「待發貨」，請至「現場訂單」管理物流
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}